import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, AlertTriangle, FileText, ChevronDown } from "lucide-react";
import { useLang } from "../context/LangContext";
import { Reveal } from "../components/site/Motion";

const Eyebrow = ({ children, dark }) => <p className="eyebrow mb-5" style={dark ? undefined : { color: "#8c6b1f" }}>{children}</p>;

const Disclaimer = ({ text, light }) => (
  <p data-testid="case0-disclaimer" className={`flex items-start gap-2 rounded-2xl border px-4 py-3 text-xs leading-relaxed md:text-sm ${light ? "border-[#d4af37]/30 bg-[#d4af37]/[0.08] text-white/80" : "border-black/15 bg-black/[0.03] text-black/70"}`}>
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#b8902f]" />{text}
  </p>
);

const Split = ({ title, rows, light }) => (
  <div className={`rounded-[24px] border p-6 ${light ? "border-white/10" : "border-black/10 bg-[#fafaf7]"}`}>
    <p className={`mb-4 font-mono text-[10px] uppercase tracking-[0.25em] ${light ? "text-white/55" : "text-black/55"}`}>{title}</p>
    <ul className="divide-y divide-black/10">
      {rows.map(([k, v]) => <li key={k} className={`flex items-center justify-between gap-4 py-3 text-sm ${light ? "divide-white/10 text-white/80" : "text-black/75"}`}><span>{k}</span><span className="font-display text-lg font-semibold text-[#8c6b1f]">{v}</span></li>)}
    </ul>
  </div>
);

const Demo = ({ d }) => {
  const [openDoc, setOpenDoc] = useState(null);
  const [shown, setShown] = useState(false);
  const tone = ["bg-[#d4af37]/15 text-[#e8c979]", "bg-[#d4af37]/15 text-[#e8c979]", "bg-white/10 text-white/70"];
  return (
    <section data-testid="case0-demo" className="grain bg-[#0a0a0b] py-20 text-[#fafaf7] md:py-28">
      <div className="wrap">
        <Eyebrow dark>{d.eyebrow}</Eyebrow>
        <p className="mb-4 inline-block rounded-full border border-[#d4af37]/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#e8c979]">{d.label}</p>
        <h2 className="max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">{d.title}</h2>
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className="rounded-[24px] border border-white/10 p-6 md:p-8" data-testid="case0-demo-request">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/55">{d.requestTitle}</p>
            <ol className="mt-5 space-y-3">
              {d.requirements.map((r, i) => <li key={r} className="flex gap-4 text-sm text-white/85 md:text-base"><span className="font-mono text-xs text-[#e8c979]">R{i + 1}</span>{r}</li>)}
            </ol>
          </div>
          <div className="rounded-[24px] border border-white/10 p-6 md:p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/55">{d.docsTitle}</p>
            <ul className="mt-5 divide-y divide-white/10">
              {d.docs.map((doc) => (
                <li key={doc.id}>
                  <button data-testid={`case0-doc-${doc.id}`} onClick={() => setOpenDoc(openDoc === doc.id ? null : doc.id)} aria-expanded={openDoc === doc.id} className="flex w-full items-center justify-between gap-3 py-3 text-left text-sm text-white/85 hover:text-[#e8c979]">
                    <span className="flex items-center gap-3"><FileText className="h-4 w-4 text-[#e8c979]" />{doc.name}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${openDoc === doc.id ? "rotate-180" : ""}`} />
                  </button>
                  {openDoc === doc.id && <p data-testid={`case0-doc-body-${doc.id}`} className="pb-4 text-sm leading-relaxed text-white/70">{doc.body}</p>}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <button data-testid="case0-demo-button" onClick={() => setShown((v) => !v)} aria-expanded={shown} className="btn-gold group mt-8 w-full justify-center sm:w-auto">
          {shown ? d.hide : d.button} <ArrowRight className={`h-4 w-4 transition-transform duration-500 ${shown ? "rotate-90" : "group-hover:translate-x-1"}`} />
        </button>
        {shown && (
          <div data-testid="case0-demo-matrix" className="mt-8 overflow-x-auto rounded-[24px] border border-[#d4af37]/30">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-white/[0.04] font-mono text-[10px] uppercase tracking-[0.25em] text-white/55">
                <tr>{d.head.map((h) => <th key={h} className="px-5 py-4 font-normal">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {d.rows.map((row, i) => (
                  <tr key={row[0]} data-testid={`case0-demo-row-${i}`}>
                    <td className="px-5 py-4 font-medium text-white/90">{row[0]}</td>
                    <td className="px-5 py-4 text-white/80"><span className={`mb-2 inline-block rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tone[i]}`}>{d.status[i]}</span><br />{row[1]}</td>
                    <td className="px-5 py-4 font-mono text-xs text-[#e8c979]">{row[2]}</td>
                    <td className="px-5 py-4 text-white/80">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t border-white/10 px-5 py-4 text-xs text-white/60">{d.footnote}</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default function CaseZero({ onRequest }) {
  const { t } = useLang();
  const c = t.case0;

  useEffect(() => {
    document.title = c.seoTitle;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = c.seoDesc;
    window.scrollTo(0, 0);
    return () => { document.title = "ByteVision — Software e AI per il New Space e la New Aviation"; };
  }, [c]);

  return (
    <main data-testid="case0-page" className="bg-[#fafaf7] text-[#0a0a0b]">
      {/* header */}
      <header className="grain relative bg-[#0a0a0b] pb-16 pt-32 text-[#fafaf7] md:pb-24 md:pt-44">
        <div className="wrap">
          <Link to="/" data-testid="case0-back-link" className="mb-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-[#e8c979]"><ArrowLeft className="h-4 w-4" />{c.back}</Link>
          <Eyebrow dark>{c.eyebrow}</Eyebrow>
          <h1 data-testid="case0-title" className="max-w-5xl font-display text-3xl font-bold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">{c.title}</h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/75 md:text-lg">{c.sub}</p>
          <div className="mt-8 max-w-3xl"><Disclaimer text={c.disclaimer} light /></div>
        </div>
      </header>

      {/* problem */}
      <section className="py-20 md:py-28" data-testid="case0-problem">
        <div className="wrap grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5"><Eyebrow>{c.problem.eyebrow}</Eyebrow><p className="text-base leading-relaxed text-black/75 md:text-lg">{c.problem.p1}</p></div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-black/55">{c.problem.stepsTitle}</p>
            <ol className="mt-4 divide-y divide-black/10">
              {c.problem.steps.map((s, i) => <li key={s} className="flex gap-5 py-3 text-sm text-black/80 md:text-base"><span className="font-display text-xl text-[#8c6b1f]">0{i + 1}</span>{s}</li>)}
            </ol>
            <p className="mt-6 text-sm leading-relaxed text-black/65 md:text-base">{c.problem.p2}</p>
          </div>
        </div>
      </section>

      {/* numbers */}
      <section className="bg-[#f3efe6] py-20 md:py-28" data-testid="case0-numbers">
        <div className="wrap">
          <Eyebrow>{c.numbers.eyebrow}</Eyebrow>
          <Disclaimer text={c.numbers.note} />
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-5">
            {c.numbers.cards.map((k) => (
              <Reveal key={k.l}><div className="rounded-[24px] border border-black/10 bg-[#fafaf7] p-5 md:p-6"><p className="font-display text-3xl font-light text-[#8c6b1f] md:text-5xl">{k.v}</p><p className="mt-2 text-xs leading-snug text-black/65 md:text-sm">{k.l}</p></div></Reveal>
            ))}
          </div>
          <div className="mt-6 md:max-w-xl"><Split title={c.numbers.splitTitle} rows={c.numbers.split} /></div>
        </div>
      </section>

      {/* solution */}
      <section className="py-20 md:py-28" data-testid="case0-solution">
        <div className="wrap grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5"><Eyebrow>{c.solution.eyebrow}</Eyebrow><p className="font-display text-2xl font-semibold tracking-tight md:text-3xl">{c.solution.intro}</p></div>
          <div className="md:col-span-6 md:col-start-7">
            <ol className="divide-y divide-black/10">
              {c.solution.steps.map((s, i) => <li key={s} className="flex gap-5 py-3 text-sm text-black/80 md:text-base"><span className="font-display text-xl text-[#8c6b1f]">0{i + 1}</span>{s}</li>)}
            </ol>
            <p className="mt-6 rounded-2xl border border-black/10 bg-black/[0.03] p-5 text-sm leading-relaxed text-black/75">{c.solution.human}</p>
          </div>
        </div>
      </section>

      {/* target scenario */}
      <section className="grain bg-[#0a0a0b] py-20 text-[#fafaf7] md:py-28" data-testid="case0-target">
        <div className="wrap">
          <Eyebrow dark>{c.target.eyebrow}</Eyebrow>
          <h2 className="max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">{c.target.title}</h2>
          <div className="mt-12 grid gap-6 lg:grid-cols-12">
            <div className="lg:col-span-4"><Split title={c.target.splitTitle} rows={c.target.split} light /></div>
            <div className="overflow-x-auto rounded-[24px] border border-white/10 lg:col-span-8" data-testid="case0-target-table">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead className="bg-white/[0.04] font-mono text-[10px] uppercase tracking-[0.25em] text-white/55"><tr>{c.target.tableHead.map((h, i) => <th key={i} className="px-5 py-4 font-normal">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-white/10">
                  {c.target.table.map((row) => <tr key={row[0]}><td className="px-5 py-4 text-white/80">{row[0]}</td><td className="px-5 py-4 font-display text-xl text-white/70">{row[1]}</td><td className="px-5 py-4 font-display text-xl text-[#e8c979]">{row[2]}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.25em] text-white/55">{c.target.resultsTitle}</p>
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="case0-results">
            {c.target.results.map(([v, l]) => <div key={l} className="rounded-[24px] border border-white/10 p-5 md:p-6"><p className="font-display text-3xl font-light gold-text md:text-5xl">{v}</p><p className="mt-2 text-xs leading-snug text-white/65 md:text-sm">{l}</p></div>)}
          </div>
          <div className="mt-6"><Disclaimer text={c.target.caveat} light /></div>
        </div>
      </section>

      <Demo d={c.demo} />

      {/* memo */}
      <section className="py-20 md:py-28" data-testid="case0-memo">
        <div className="wrap">
          <Eyebrow>{c.memo.eyebrow}</Eyebrow>
          <h2 className="max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">{c.memo.title}</h2>
          <div className="mt-10 overflow-hidden rounded-[28px] border border-black/10 bg-[#fafaf7]">
            <dl className="divide-y divide-black/10">
              {c.memo.sections.map(([k, v]) => <div key={k} className="grid gap-2 px-6 py-5 md:grid-cols-12 md:px-8"><dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8c6b1f] md:col-span-3">{k}</dt><dd className="text-sm leading-relaxed text-black/80 md:col-span-9 md:text-base">{v}</dd></div>)}
            </dl>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div className="rounded-[24px] border border-black/10 p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-black/55">{c.memo.roadmapTitle}</p>
              <ol className="mt-4 divide-y divide-black/10">{c.memo.roadmap.map(([w, v]) => <li key={w} className="flex gap-5 py-3 text-sm md:text-base"><span className="w-28 shrink-0 font-semibold text-[#8c6b1f]">{w}</span><span className="text-black/80">{v}</span></li>)}</ol>
              <p className="mt-4 text-xs text-black/65">{c.memo.roadmapNote}</p>
            </div>
            <div className="rounded-[24px] border border-black/10 p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-black/55">{c.memo.validationTitle}</p>
              <p className="mt-4 text-sm leading-relaxed text-black/80 md:text-base">{c.memo.validation}</p>
            </div>
          </div>
        </div>
      </section>

      {/* final cta */}
      <section className="grain bg-[#0a0a0b] py-20 text-[#fafaf7] md:py-28" data-testid="case0-final-cta">
        <div className="wrap grid gap-8 md:grid-cols-12 md:items-center">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl md:col-span-8">{c.finalCta.title}</h2>
          <div className="flex flex-col gap-4 md:col-span-4 md:items-end">
            <button data-testid="case0-cta-diagnosis" onClick={() => onRequest("diagnosis")} className="btn-gold group w-full justify-center md:w-auto">{c.finalCta.cta} <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" /></button>
            <Link to="/#offerte" data-testid="case0-cta-offers" className="btn-ghost w-full justify-center md:w-auto">{c.finalCta.secondary}</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
